import type { IntegrationRequestResult, IntegrationRequestStep } from './IntegrationRun';
import { z } from 'zod';
import {
  addWebhookHeaderIssues,
  webhookHeaderRowSchema,
  webhookHeaderRowsFromNames,
  webhookHeaderRowsToPayload,
} from './WebhookHeaders';

export enum IntegrationRequestStatus {
  PENDING = 'PENDING',
  OBSERVED = 'OBSERVED',
  IN_PROGRESS = 'IN_PROGRESS',
  INTEGRATED = 'INTEGRATED',
  DOC_APPROVED = 'DOC_APPROVED',
  SENT = 'SENT',
  CANCELLED = 'CANCELLED',
}

export type IntegrationRequestType = 'API' | 'WEBHOOK';

export const INTEGRATION_REQUEST_TYPE_LABELS: Record<IntegrationRequestType, string> = {
  API: 'Integración API',
  WEBHOOK: 'Webhook',
};

export const INTEGRATION_REQUEST_STATUS_LABELS: Record<IntegrationRequestStatus, string> = {
  [IntegrationRequestStatus.PENDING]: 'Pendiente',
  [IntegrationRequestStatus.OBSERVED]: 'Observada',
  [IntegrationRequestStatus.IN_PROGRESS]: 'En curso',
  [IntegrationRequestStatus.INTEGRATED]: 'Integrada',
  [IntegrationRequestStatus.DOC_APPROVED]: 'Doc Aprobada',
  [IntegrationRequestStatus.SENT]: 'Enviado a Cliente',
  [IntegrationRequestStatus.CANCELLED]: 'Cancelada',
};


export const INTEGRATION_REQUEST_STATUS_COLORS: Record<
  IntegrationRequestStatus,
  { backgroundColor: string; ballColor: string; textColor: string }
> = {
  [IntegrationRequestStatus.PENDING]: { backgroundColor: '#FFF8E1', ballColor: '#B26A00', textColor: '#B26A00' },
  [IntegrationRequestStatus.OBSERVED]: { backgroundColor: '#FFEBEE', ballColor: '#C62828', textColor: '#C62828' },
  [IntegrationRequestStatus.IN_PROGRESS]: { backgroundColor: '#E3F2FD', ballColor: '#1565C0', textColor: '#1565C0' },
  [IntegrationRequestStatus.INTEGRATED]: { backgroundColor: '#E8F5E9', ballColor: '#2E7D32', textColor: '#2E7D32' },
  [IntegrationRequestStatus.DOC_APPROVED]: { backgroundColor: '#E0F2F1', ballColor: '#00695C', textColor: '#00695C' },
  [IntegrationRequestStatus.SENT]: { backgroundColor: '#EDE7F6', ballColor: '#4527A0', textColor: '#4527A0' },
  [IntegrationRequestStatus.CANCELLED]: { backgroundColor: '#F5F5F5', ballColor: '#757575', textColor: '#616161' },
};

export const ACTIVE_INTEGRATION_REQUEST_STATUSES: IntegrationRequestStatus[] = [
  IntegrationRequestStatus.PENDING,
  IntegrationRequestStatus.OBSERVED,
  IntegrationRequestStatus.IN_PROGRESS,
  IntegrationRequestStatus.INTEGRATED,
  IntegrationRequestStatus.DOC_APPROVED,
];

export const SENDABLE_STATUS_BY_TYPE: Record<IntegrationRequestType, IntegrationRequestStatus> = {
  API: IntegrationRequestStatus.DOC_APPROVED,
  WEBHOOK: IntegrationRequestStatus.INTEGRATED,
};

export const ACTIVE_STATUS_FILTER = 'ACTIVE';
export const ALL_STATUS_FILTER = 'ALL';

export interface IntegrationRequestFilters {
  status: string;
  type: IntegrationRequestType | '';
}

export const DEFAULT_INTEGRATION_REQUEST_FILTERS: IntegrationRequestFilters = {
  status: ALL_STATUS_FILTER,
  type: '',
};

export const statusesForFilter = (status: string): IntegrationRequestStatus[] => {
  if (status === ACTIVE_STATUS_FILTER) return ACTIVE_INTEGRATION_REQUEST_STATUSES;
  if (status === ALL_STATUS_FILTER || !status) return [];
  return [status as IntegrationRequestStatus];
};

export const EDITABLE_INTEGRATION_REQUEST_STATUSES: IntegrationRequestStatus[] = [
  IntegrationRequestStatus.PENDING,
  IntegrationRequestStatus.OBSERVED,
];

export const OBSERVABLE_INTEGRATION_REQUEST_STATUSES: IntegrationRequestStatus[] = [
  IntegrationRequestStatus.PENDING,
  IntegrationRequestStatus.IN_PROGRESS,
];

export interface IntegrationRequestWebhook {
  url: string;
  headerNames: string[];
  statusIds: string[];
}

export interface IntegrationRequestWebhookPayload {
  url: string;
  headers: Record<string, string> | null;
  statusIds: string[];
}

export interface IntegrationRequestCodeRef {
  id: string;
  name: string;
  code: string | null;
}

export interface IntegrationRequest {
  id: string;
  type: IntegrationRequestType;
  status: IntegrationRequestStatus;
  commerceId: string;
  logisticOperatorId: string;
  branchIds: string[] | null;
  serviceWithOperatorIds: string[];
  defaultServiceWithOperatorId: string | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  goLiveDate: string | null;
  hasLegacyOrders: boolean;
  webhook: IntegrationRequestWebhook | null;
  notes: string | null;
  observation: string | null;
  requestedByName: string | null;
  handledByName: string | null;
  steps: IntegrationRequestStep[];
  result: IntegrationRequestResult | null;
  createdAt: string;
  updatedAt: string;
  commerce: IntegrationRequestCodeRef | null;
  logisticOperator: IntegrationRequestCodeRef | null;
}

export interface IntegrationRequestBranchView {
  id: string;
  name: string;
  externalBranchCode: string | null;
  apiCode: string | null;
}

export interface IntegrationRequestDetail extends IntegrationRequest {
  allBranches: boolean;
  branches: IntegrationRequestBranchView[];
  services: { id: string; name: string; apiCode: string | null; isDefault: boolean }[];
  webhookStatuses: { id: string; name: string }[];
}

export interface IntegrationRequestFormOptions {
  commerce: IntegrationRequestCodeRef & { integrated: boolean };
  logisticOperators: (IntegrationRequestCodeRef & {
    services: {
      id: string;
      name: string;
      apiCode: string | null;
      enabledForCommerce: boolean;
    }[];
    currentServiceCodes: string[] | null;
  })[];
  branches: IntegrationRequestBranchView[];
  notifiableStatuses: {
    id: string;
    name: string;
    commercialName: string | null;
    statusEventId: string | null;
  }[];
  activeWebhooks: { statusId: string; urls: string[] }[];
  openRequests: Pick<IntegrationRequest, 'id' | 'type' | 'status' | 'createdAt'>[];
}

export interface IntegrationRequestPayload {
  type: IntegrationRequestType;
  commerceId: string;
  logisticOperatorId: string;
  branchIds: string[] | null;
  serviceWithOperatorIds: string[];
  contactName: string;
  contactEmail: string;
  contactPhone: string | null;
  goLiveDate: string | null;
  hasLegacyOrders: boolean;
  webhook: IntegrationRequestWebhookPayload | null;
  notes: string | null;
}

const GO_LIVE_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const integrationRequestFormSchema = z
  .object({
    type: z.enum(['API', 'WEBHOOK']),
    logisticOperatorId: z.string().min(1, 'Seleccione el operador logístico'),
    allBranches: z.boolean(),
    branchIds: z.array(z.string()),
    serviceWithOperatorIds: z.array(z.string()),
    contactName: z
      .string()
      .trim()
      .min(2, 'Ingrese el nombre del contacto técnico')
      .max(150, 'El nombre no puede superar 150 caracteres'),
    contactEmail: z.string().trim().email('Ingrese un email válido'),
    contactPhone: z.string().trim().max(30, 'El teléfono no puede superar 30 caracteres'),
    goLiveDate: z
      .string()
      .trim()
      .refine((value) => value === '' || GO_LIVE_DATE_REGEX.test(value), 'El formato debe ser AAAA-MM-DD'),
    hasLegacyOrders: z.boolean(),
    withWebhook: z.boolean(),
    webhookUrl: z.string().trim(),
    webhookHeaders: z.array(webhookHeaderRowSchema),
    webhookStatusIds: z.array(z.string()),
    notes: z.string().trim().max(2000, 'Las notas no pueden superar 2000 caracteres'),
  })
  .superRefine((data, ctx) => {
    if (data.type === 'API') {
      if (data.serviceWithOperatorIds.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['serviceWithOperatorIds'], message: 'Seleccione al menos un servicio' });
      }
      if (!data.allBranches && data.branchIds.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['branchIds'], message: 'Seleccione al menos una sucursal o marque todas' });
      }
    }

    if (data.type === 'WEBHOOK' || data.withWebhook) {
      if (!/^https:\/\/\S+$/i.test(data.webhookUrl)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['webhookUrl'], message: 'Ingrese una URL https válida' });
      }
      if (data.webhookStatusIds.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['webhookStatusIds'], message: 'Seleccione al menos un estado a notificar' });
      }
      addWebhookHeaderIssues(data.webhookHeaders, 'webhookHeaders', ctx);
    }
  });

export type IntegrationRequestFormSchema = z.infer<typeof integrationRequestFormSchema>;

export const defaultIntegrationRequestFormValues: IntegrationRequestFormSchema = {
  type: 'API',
  logisticOperatorId: '',
  allBranches: true,
  branchIds: [],
  serviceWithOperatorIds: [],
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  goLiveDate: '',
  hasLegacyOrders: false,
  withWebhook: false,
  webhookUrl: '',
  webhookHeaders: [],
  webhookStatusIds: [],
  notes: '',
};

export const integrationRequestToFormValues = (
  request: IntegrationRequest
): IntegrationRequestFormSchema => ({
  type: request.type,
  logisticOperatorId: request.logisticOperatorId,
  allBranches: !request.branchIds,
  branchIds: request.branchIds ?? [],
  serviceWithOperatorIds: request.serviceWithOperatorIds,
  contactName: request.contactName,
  contactEmail: request.contactEmail,
  contactPhone: request.contactPhone ?? '',
  goLiveDate: request.goLiveDate ?? '',
  hasLegacyOrders: request.hasLegacyOrders,
  withWebhook: !!request.webhook,
  webhookUrl: request.webhook?.url ?? '',
  webhookHeaders: webhookHeaderRowsFromNames(request.webhook?.headerNames ?? []),
  webhookStatusIds: request.webhook?.statusIds ?? [],
  notes: request.notes ?? '',
});

export const integrationRequestFormToPayload = (
  commerceId: string,
  form: IntegrationRequestFormSchema
): IntegrationRequestPayload => {
  const isApi = form.type === 'API';
  const sendsWebhook = form.type === 'WEBHOOK' || form.withWebhook;

  return {
    type: form.type,
    commerceId,
    logisticOperatorId: form.logisticOperatorId,
    branchIds: isApi && !form.allBranches ? form.branchIds : null,
    serviceWithOperatorIds: isApi ? form.serviceWithOperatorIds : [],
    contactName: form.contactName.trim(),
    contactEmail: form.contactEmail.trim(),
    contactPhone: form.contactPhone.trim() || null,
    goLiveDate: form.goLiveDate.trim() || null,
    hasLegacyOrders: form.hasLegacyOrders,
    webhook: sendsWebhook
      ? {
          url: form.webhookUrl.trim(),
          headers: webhookHeaderRowsToPayload(form.webhookHeaders),
          statusIds: form.webhookStatusIds,
        }
      : null,
    notes: form.notes.trim() || null,
  };
};
